/**
 * Stripe Ops — 1-click admin repair panel.
 *
 * Solves the "prod checkout is 503, run the PowerShell script from your
 * laptop with an admin token" problem: super-admin logs into the deployed
 * site, opens this panel, clicks a button. The browser is already
 * authenticated via the JWT stored in localStorage — no token to paste,
 * no PowerShell to install, no laptop-side script to run.
 *
 * Two-stage workflow enforced client-side:
 *   1. Dry-run: POST /api/admin/stripe/auto-discover  {apply:false}
 *   2. If dry-run looks correct, admin clicks Apply -> POST with
 *      {apply:true}. Nothing is written to prod DB until step 2.
 *
 * A follow-up "Verify" step polls /startup-check and reports zero
 * failures before the panel considers itself green.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle, CheckCircle2, Loader2, RefreshCw, ShieldAlert,
  ClipboardCheck, PlayCircle, ChevronDown, ChevronRight,
} from "lucide-react";
import { api } from "@/lib/api";
import { errorMessage } from "@/lib/errors";

const cx = (...args) => args.filter(Boolean).join(" ");

// Flatten the /diagnose response into a flat list of rows so we can render
// them in a table. Backend returns `{tiers: {pro: {periods: {monthly: {...}, yearly: {...}}, ...}, premium: {...}}}`.
function flattenDiag(diag) {
  const out = [];
  const tiers = diag?.tiers || {};
  for (const tier of Object.keys(tiers)) {
    const tierBlock = tiers[tier];
    const periods = tierBlock?.periods || {};
    for (const period of Object.keys(periods)) {
      const p = periods[period] || {};
      // A row is "healthy" when Stripe agrees the price exists, it's active,
      // recurring interval matches, and the Mongo row is marked active.
      const active = p.row_active === true && p.exists_on_stripe === true && p.stale !== true;
      out.push({
        tier,
        period,
        stripe_price_id: p.cached_price_id,
        exists_on_stripe: p.exists_on_stripe,
        active,
        unit_amount: p.unit_amount,
        currency: p.currency,
        interval: p.recurring_interval,
        stale_reasons: p.stale_reasons || [],
        stripe_error_type: p.stripe_error_type,
      });
    }
  }
  return out;
}

export default function AdminStripeOps() {
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [dryRun, setDryRun] = useState(null);
  const [dryLoading, setDryLoading] = useState(false);
  const [applied, setApplied] = useState(null);
  const [applyLoading, setApplyLoading] = useState(false);
  const [startup, setStartup] = useState(null);
  const [expandRaw, setExpandRaw] = useState({});

  useEffect(() => { runDiagnose(); }, []);

  const runDiagnose = async () => {
    setDiagLoading(true);
    try {
      const { data } = await api.get("/admin/stripe/diagnose");
      setDiag(data);
    } catch (e) {
      toast.error(errorMessage(e, "Diagnose failed"));
    } finally {
      setDiagLoading(false);
    }
  };

  const runDryRun = async () => {
    setDryLoading(true);
    setDryRun(null);
    setApplied(null);
    try {
      const { data } = await api.post("/admin/stripe/auto-discover", {
        apply: false,
        force_product_ids: {},
      });
      setDryRun(data);
      toast.success("Dry-run complete — review below, then click Apply");
    } catch (e) {
      toast.error(errorMessage(e, "Auto-discover dry-run failed"));
    } finally {
      setDryLoading(false);
    }
  };

  const runApply = async () => {
    if (!dryRun) return;
    if (
      !window.confirm(
        "Apply this discovered price map to production?\n\n" +
        "This will REWRITE `stripe_price_map` in the current environment's " +
        "Mongo. Only proceed if the dry-run above looks correct."
      )
    ) {
      return;
    }
    setApplyLoading(true);
    try {
      const { data } = await api.post("/admin/stripe/auto-discover", {
        apply: true,
        force_product_ids: {},
      });
      setApplied(data);
      toast.success("Price map applied. Running verification…");
      await runDiagnose();
      await runVerify();
    } catch (e) {
      toast.error(errorMessage(e, "Apply failed"));
    } finally {
      setApplyLoading(false);
    }
  };

  const runVerify = async () => {
    try {
      const { data } = await api.get("/admin/stripe/startup-check");
      setStartup(data);
      const failsList = Array.isArray(data?.failures) ? data.failures : [];
      if (failsList.length === 0 && data?.status !== "not_yet_run") {
        toast.success("Startup check clean — production checkout is healthy");
      } else if (data?.status === "not_yet_run") {
        toast.info("Startup check hasn't run since last boot. Restart the backend if needed.");
      } else {
        toast.error(`Still ${failsList.length} failure(s) after apply — see details`);
      }
    } catch (e) {
      toast.error(errorMessage(e, "Startup check failed"));
    }
  };

  const rows = useMemo(() => flattenDiag(diag), [diag]);
  const activeCount = rows.filter((r) => r.active).length;
  const totalRows = rows.length;
  const priceMapHealthy = totalRows > 0 && activeCount === totalRows;

  const mode = diag?.stripe_env?.mode;
  const modeLabel = mode ? String(mode).toUpperCase() : "…";
  const modeBad = mode && mode === "placeholder";

  const startupFailures = Array.isArray(startup?.failures) ? startup.failures.length : null;
  const startupTone =
    startup?.status === "not_yet_run" ? "warn"
      : startupFailures === 0 ? "ok"
        : startupFailures != null ? "bad"
          : "warn";
  const startupValue =
    startup?.status === "not_yet_run" ? "not run"
      : startupFailures === 0 ? "healthy"
        : startupFailures != null ? `${startupFailures} fail`
          : "—";

  return (
    <div className="card-flat p-6 mb-8" data-testid="admin-stripe-ops">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            Ops · Stripe
          </div>
          <h2 className="font-display font-bold text-2xl mt-1 tracking-tight">
            Stripe Price-Map Repair
          </h2>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            When production checkout returns a friendly 503 with a{" "}
            <code className="text-xs font-mono px-1 rounded bg-muted">chk_...</code>{" "}
            reference, the price map has drifted. Rebuild it from Stripe in
            two clicks. Nothing is written to Mongo until you press{" "}
            <strong>Apply</strong>.
          </p>
        </div>
        <button
          type="button"
          data-testid="stripe-ops-refresh-diagnose"
          onClick={runDiagnose}
          disabled={diagLoading}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <RefreshCw size={14} className={diagLoading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5" data-testid="stripe-ops-env">
        <EnvTile label="Stripe mode" value={modeLabel} tone={modeBad ? "warn" : "ok"} />
        <EnvTile
          label="Configured"
          value={diag?.stripe_env?.configured ? "yes" : "NO"}
          tone={diag?.stripe_env?.configured ? "ok" : "bad"}
        />
        <EnvTile
          label="Price rows"
          value={diag ? `${activeCount}/${totalRows} active` : "…"}
          tone={priceMapHealthy ? "ok" : "warn"}
        />
        <EnvTile
          label="Startup"
          value={startupValue}
          tone={startupTone}
        />
      </div>

      <Step
        number={1}
        title="Diagnose"
        state={diag ? "done" : diagLoading ? "loading" : "idle"}
      >
        {diag ? (
          <PriceMapTable rows={rows} />
        ) : (
          <div className="text-sm text-muted-foreground">Loading diagnose…</div>
        )}
        <RawJsonToggle
          data={diag}
          open={!!expandRaw.diag}
          toggle={() => setExpandRaw((s) => ({ ...s, diag: !s.diag }))}
          testId="stripe-ops-raw-diag"
        />
      </Step>

      <Step
        number={2}
        title="Auto-discover (dry-run)"
        state={dryRun ? "done" : dryLoading ? "loading" : "idle"}
        action={
          <button
            type="button"
            data-testid="stripe-ops-dryrun-btn"
            onClick={runDryRun}
            disabled={dryLoading || applyLoading}
            className="inline-flex items-center gap-2 px-4 h-9 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 text-sm font-medium transition-colors"
          >
            {dryLoading ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <PlayCircle size={14} />
            )}
            {dryRun ? "Re-run dry-run" : "Run dry-run"}
          </button>
        }
      >
        {dryRun ? (
          <DryRunReport report={dryRun} />
        ) : (
          <p className="text-sm text-muted-foreground">
            Scans your Stripe account for products tagged{" "}
            <code className="text-xs font-mono px-1 rounded bg-muted">metadata.tier=pro</code>{" "}
            and{" "}
            <code className="text-xs font-mono px-1 rounded bg-muted">tier=premium</code>,
            harvests monthly + yearly prices, and shows what the new map
            <em> would be</em>. Nothing is written yet.
          </p>
        )}
        <RawJsonToggle
          data={dryRun}
          open={!!expandRaw.dry}
          toggle={() => setExpandRaw((s) => ({ ...s, dry: !s.dry }))}
          testId="stripe-ops-raw-dry"
        />
      </Step>

      <Step
        number={3}
        title="Apply"
        state={
          applied ? "done" : applyLoading ? "loading" : dryRun ? "ready" : "blocked"
        }
        action={
          <button
            type="button"
            data-testid="stripe-ops-apply-btn"
            onClick={runApply}
            disabled={!dryRun || applyLoading || applied}
            className={cx(
              "inline-flex items-center gap-2 px-4 h-9 rounded-md text-sm font-medium transition-colors",
              !dryRun || applied
                ? "bg-muted text-muted-foreground cursor-not-allowed"
                : "bg-emerald-600 text-white hover:bg-emerald-500"
            )}
          >
            {applyLoading ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <ClipboardCheck size={14} />
            )}
            {applied ? "Applied" : "Apply to production"}
          </button>
        }
      >
        {!dryRun && (
          <p className="text-sm text-muted-foreground">
            Run the dry-run first to see what will change.
          </p>
        )}
        {dryRun && !applied && (
          <div
            className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-100"
            data-testid="stripe-ops-apply-warning"
          >
            <ShieldAlert size={14} className="inline mr-1" />
            <strong>You are about to rewrite the production price map.</strong>{" "}
            Confirm every row in the dry-run above is the correct{" "}
            <code className="text-xs font-mono px-1 rounded bg-black/20">price_XXX</code>{" "}
            for the current Stripe {modeLabel} account before clicking Apply.
          </div>
        )}
        {applied && (
          <div
            className="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-100 space-y-1"
            data-testid="stripe-ops-apply-result"
          >
            <div>
              <CheckCircle2 size={14} className="inline mr-1" />
              Wrote <span className="font-mono">{applied.changes ?? 0}</span> row(s).
            </div>
            {startup && (
              <div>
                Startup check:{" "}
                <span
                  className={cx(
                    "font-mono",
                    startupFailures === 0 ? "text-emerald-300" : "text-red-300"
                  )}
                >
                  {startupFailures === 0
                    ? "healthy"
                    : `${startupFailures} failure(s)`}
                </span>
              </div>
            )}
          </div>
        )}
        <RawJsonToggle
          data={applied}
          open={!!expandRaw.apply}
          toggle={() => setExpandRaw((s) => ({ ...s, apply: !s.apply }))}
          testId="stripe-ops-raw-apply"
        />
      </Step>

      <Step
        number={4}
        title="Verify"
        state={
          startup
            ? (startupFailures === 0 ? "done" : "bad")
            : "idle"
        }
        action={
          <button
            type="button"
            data-testid="stripe-ops-verify-btn"
            onClick={runVerify}
            className="inline-flex items-center gap-2 px-4 h-9 rounded-md border border-input hover:bg-accent hover:text-accent-foreground text-sm font-medium transition-colors"
          >
            <RefreshCw size={14} /> Verify now
          </button>
        }
      >
        {startup ? (
          <div
            className={cx(
              "text-sm",
              startupFailures === 0 ? "text-emerald-500" : "text-red-500"
            )}
            data-testid="stripe-ops-verify-verdict"
          >
            {startupFailures === 0 ? (
              <>
                <CheckCircle2 size={14} className="inline mr-1" />
                All price-map rows resolve against Stripe. Checkout is healthy.
              </>
            ) : (
              <>
                <AlertTriangle size={14} className="inline mr-1" />
                Startup check reports {startupFailures} failure(s). Expand the
                raw JSON below for details.
              </>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            After Apply, this will re-run <code>/api/admin/stripe/startup-check</code>{" "}
            and report the final state.
          </p>
        )}
        <RawJsonToggle
          data={startup}
          open={!!expandRaw.startup}
          toggle={() => setExpandRaw((s) => ({ ...s, startup: !s.startup }))}
          testId="stripe-ops-raw-startup"
        />
      </Step>
    </div>
  );
}

/* --------------------------- sub-components --------------------------- */

function EnvTile({ label, value, tone }) {
  const toneCls =
    tone === "ok" ? "text-emerald-500"
      : tone === "warn" ? "text-amber-500"
        : tone === "bad" ? "text-red-500"
          : "text-foreground";
  return (
    <div className="rounded-md border border-border/60 bg-background/60 p-3">
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
        {label}
      </div>
      <div className={cx("font-display text-base font-bold mt-1", toneCls)}>
        {value}
      </div>
    </div>
  );
}

function Step({ number, title, state, children, action }) {
  const icon =
    state === "loading" ? <Loader2 size={16} className="animate-spin text-muted-foreground" />
      : state === "done" ? <CheckCircle2 size={16} className="text-emerald-500" />
        : state === "bad" ? <AlertTriangle size={16} className="text-red-500" />
          : state === "ready" ? <PlayCircle size={16} className="text-primary" />
            : <span className="w-4 h-4 rounded-full border border-border inline-block" />;
  return (
    <div
      className="mt-5 border border-border/60 rounded-lg overflow-hidden"
      data-testid={`stripe-ops-step-${number}`}
    >
      <div className="flex items-center justify-between gap-3 p-3 border-b border-border/60 bg-background/40">
        <div className="flex items-center gap-3">
          {icon}
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              Step {number}
            </div>
            <div className="font-medium">{title}</div>
          </div>
        </div>
        {action}
      </div>
      <div className="p-4 space-y-3">{children}</div>
    </div>
  );
}

function PriceMapTable({ rows }) {
  if (!rows.length) {
    return (
      <div className="text-sm text-red-500">
        <AlertTriangle size={14} className="inline mr-1" />
        No rows in the price map yet. Run the dry-run below to populate it.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto" data-testid="stripe-ops-price-map-table">
      <table className="w-full text-xs">
        <thead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground border-b border-border">
          <tr>
            <th className="text-left py-1.5 px-2">Tier</th>
            <th className="text-left py-1.5 px-2">Period</th>
            <th className="text-left py-1.5 px-2">Price ID</th>
            <th className="text-left py-1.5 px-2">Amount</th>
            <th className="text-left py-1.5 px-2">Status</th>
            <th className="text-left py-1.5 px-2">Notes</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={`${r.tier}-${r.period}-${i}`}
              className="border-b border-border/40"
              data-testid={`stripe-ops-row-${r.tier}-${r.period}`}
            >
              <td className="py-1.5 px-2 font-mono">{r.tier}</td>
              <td className="py-1.5 px-2 font-mono">{r.period}</td>
              <td className="py-1.5 px-2 font-mono text-[10px] break-all">
                {r.stripe_price_id || "—"}
              </td>
              <td className="py-1.5 px-2 font-mono">
                {r.unit_amount != null
                  ? `${(r.unit_amount / 100).toFixed(2)} ${(r.currency || "").toUpperCase()}`
                  : "—"}
              </td>
              <td className="py-1.5 px-2">
                {r.active ? (
                  <span className="inline-flex items-center gap-1 text-emerald-500">
                    <CheckCircle2 size={12} /> active
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-red-500">
                    <AlertTriangle size={12} /> broken
                  </span>
                )}
              </td>
              <td className="py-1.5 px-2 font-mono text-muted-foreground text-[10px]">
                {r.stripe_error_type
                  ? `stripe_error:${r.stripe_error_type}`
                  : (r.stale_reasons || []).join(", ") || "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DryRunReport({ report }) {
  const tiers = report?.tiers || {};
  const warnings = report?.warnings || [];
  return (
    <div className="space-y-3" data-testid="stripe-ops-dryrun-report">
      <div className="text-xs text-muted-foreground">
        Scanned{" "}
        {Object.entries(report.scan_summary || {})
          .map(([t, n]) => `${t}: ${n} product${n === 1 ? "" : "s"}`)
          .join(" · ")}
      </div>
      {Object.keys(tiers).map((tier) => (
        <TierProposal key={tier} tier={tier} tierBlock={tiers[tier]} />
      ))}
      {warnings.length > 0 && (
        <div
          className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-100"
          data-testid="stripe-ops-warnings"
        >
          <div className="font-semibold mb-1">
            <AlertTriangle size={12} className="inline mr-1" />
            {warnings.length} warning(s):
          </div>
          <ul className="list-disc pl-5 space-y-0.5">
            {warnings.map((w, i) => (
              <li key={i}>
                {w.warning}
                {w.tier ? ` — ${w.tier}` : ""}
                {w.hint ? ` · ${w.hint}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function TierProposal({ tier, tierBlock }) {
  const periods = tierBlock?.periods || {};
  return (
    <div className="rounded-md border border-border/60 p-3 text-xs" data-testid={`stripe-ops-proposal-${tier}`}>
      <div className="font-semibold uppercase text-muted-foreground mb-1">{tier}</div>
      <div className="font-mono text-[11px] text-foreground/80 break-all">
        Product: {tierBlock.canonical_product_id || tierBlock.chosen_product_id || "—"} · Source: {tierBlock.product_source || "—"}
      </div>
      <ul className="mt-1 space-y-0.5 font-mono text-[11px]">
        {Object.keys(periods).map((period) => {
          const p = periods[period] || {};
          const price = p.proposed_price_id || p.chosen_price_id || "—";
          const amt = p.proposed_amount ?? p.chosen_price?.unit_amount;
          const ccy = p.proposed_currency ?? p.chosen_price?.currency;
          const action = p.action || "—";
          const actionColor =
            action === "unchanged" ? "text-muted-foreground"
              : action === "pinned" ? "text-emerald-400"
                : action === "would_pin" ? "text-amber-300"
                  : "text-red-400";
          return (
            <li key={period}>
              {period}:{" "}
              <span className="text-emerald-400">{price}</span>
              {amt != null && (
                <span className="text-muted-foreground">
                  {" "}· {(amt / 100).toFixed(2)} {(ccy || "").toUpperCase()}
                </span>
              )}
              <span className={cx("ml-2", actionColor)}>[{action}]</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function RawJsonToggle({ data, open, toggle, testId }) {
  if (!data) return null;
  return (
    <div>
      <button
        type="button"
        onClick={toggle}
        data-testid={`${testId}-toggle`}
        className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
      >
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        Raw JSON
      </button>
      {open && (
        <pre
          className="mt-2 max-h-64 overflow-auto text-[10px] font-mono bg-black/40 border border-border/60 rounded p-2"
          data-testid={testId}
        >
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
}
